import React, {useCallback} from 'react';
import useData from "../../../../hooks/useData";
import {Box, Grid, useTheme, Typography} from "@mui/material";
import {ContextState} from "../../../../model/contextState";
import {CustomTheme} from "../../../../config/theme/theme";
import {SiteDataResponse, SiteReadingResponse} from "../../../../services";
import {isMobile} from "react-device-detect";
import {useTranslation} from "react-i18next";
import {Card} from "../../../../components/card";

export function Statistics() {
  const context: ContextState | null = useData();
  const data: SiteDataResponse = context?.data;

  const theme: CustomTheme = useTheme();
  const {t} = useTranslation();

  function getTotalProduction(): number {
    return data?.readings?.reduce(function (a: number, b: SiteReadingResponse) {
      return a + ((b.production / 4) / 1000);
    }, 0)
  }

  function getProduction(): number {
    return Math.round(data?.readings?.reduce(function (a: number, b: SiteReadingResponse) {
      return a + ((b.uncurtailedProduction / 4) / 1000);
    }, 0));
  }

  function getCurtailment(): number {
    return Math.round(data?.readings?.reduce(function (a: number, b: SiteReadingResponse) {
      return a + ((b.curtailedProduction / 4) / 1000);
    }, 0));
  }

  function getTotalConsumption(): number {
    return Math.round(data?.readings?.reduce(function (a: number, b: SiteReadingResponse) {
      return a + ((b.consumption / 4) / 1000);
    }, 0))
  }

  function getTotalGridConsumption(): number {
    return Math.round(data?.readings?.reduce(function (a: number, b: SiteReadingResponse) {
      return a + ((b.gridOfftake / 4) / 1000);
    }, 0))
  }

  function getTotalGridInjection(): number {
    return Math.round(-data?.readings?.reduce(function (a: number, b: SiteReadingResponse) {
      return a + ((b.gridInject / 4) / 1000);
    }, 0))
  }

  function getTotalBatteryCharge(): number {
    return Math.round(data?.readings?.reduce(function (a: number, b: SiteReadingResponse) {
      return a + b.flexCharge;
    }, 0) / 4000);
  }

  function getTotalBatteryDischarge(): number {
    return Math.round(data?.readings?.reduce(function (a: number, b: SiteReadingResponse) {
      return a + b.flexDischarge;
    }, 0) / 4000);
  }

  const renderStatistic = useCallback((description, value) => {
    return <Card xs={12} sm={6} xl={3} sx={{padding: isMobile ? 2 : 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '120px'}}>
      <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
        <Typography fontSize={isMobile ? "0.85em" : "0.9em"} fontWeight="600" color={theme.palette.text.primary} textAlign="center">
          {description}
        </Typography>
        <Typography fontSize={isMobile ? "1.5em" : "1.8em"} fontWeight="700" color={theme.palette.text.primary}>
          {value} kWh
        </Typography>
      </Box>
    </Card>
  }, [])

  const renderBidirectionalStatistic = useCallback((title, description1, description2, value1, value2) => {
    return <Card xs={12} sm={6} xl={3} sx={{padding: isMobile ? 2 : 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '120px'}}>
      <Box display="flex" flexDirection="column" alignItems="center" gap={0.5} width="100%">
        <Typography fontSize={isMobile ? "0.85em" : "0.9em"} fontWeight="600" color={theme.palette.text.primary} textAlign="center" sx={{marginBottom: 0.5}}>
          {title}
        </Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" px={isMobile ? 1 : 2}>
          <Typography fontSize={isMobile ? "0.75em" : "0.8em"} fontWeight="600" color={theme.palette.text.primary}>
            {description1}
          </Typography>
          <Typography fontSize={isMobile ? "1.1em" : "1.2em"} fontWeight="700" color={theme.palette.text.primary}>
            {value1} kWh
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" px={isMobile ? 1 : 2}>
          <Typography fontSize={isMobile ? "0.75em" : "0.8em"} fontWeight="600" color={theme.palette.text.primary}>
            {description2}
          </Typography>
          <Typography fontSize={isMobile ? "1.1em" : "1.2em"} fontWeight="700" color={theme.palette.text.primary}>
            {value2} kWh
          </Typography>
        </Box>
      </Box>
    </Card>
  }, [])

  return <>
    {renderStatistic(t('dashboard.technical.consumption'), data ? getTotalConsumption() : 0)}
    {renderBidirectionalStatistic(t('dashboard.technical.production'), t('dashboard.technical.effectiveProduction'), t('dashboard.technical.curtailment'), data ? getProduction() : 0, data ? getCurtailment() : 0)}
    {renderBidirectionalStatistic(t('common.battery'), t('dashboard.technical.charge'),  t('dashboard.technical.discharge'), data ? getTotalBatteryCharge() : 0, data ? getTotalBatteryDischarge() : 0)}
    {renderBidirectionalStatistic(t('common.net'),  t('dashboard.technical.offtake'),  t('dashboard.technical.injection'), data ? getTotalGridConsumption() : 0, data ? getTotalGridInjection() : 0)}
  </>
}